/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shape, string } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import {
    getTalonOneRewardsLoading,
    getTalonOneRewardsError,
    getTalonOneRewardsData,
} from '../../../../../state/ducks/Member/ducks/TalonOneRewards/TalonOneRewards-Selectors';
import { fetchTalonOneRewards, clearTalonOneRewards } from '../../../../../state/ducks/Member/ducks/TalonOneRewards/TalonOneRewards-Actions';
import { getIsAuthenticatedStatus } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import findContent from '../../../../gql/queries/findContent';

const styles = ({ breakpoints }) => ({
    accountBannerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 8px',
        // backgroundColor is dynamically set via inline style
        height: 'auto !important',
        fontSize: '16px!important',
        gap: '8px',
        // Mobile padding
        [breakpoints.down('sm')]: {
            padding: '16px',
            fontSize: '14px!important',
        },
    },
    bannerIcon: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& img': {
            width: '41px',
            height: '39px',
        },
    },
    bannerTextContent: {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        wordBreak: 'break-word',
        '& p': {
            margin: '0px!important',
        },
    },
    accountNameText: {
        fontFamily: 'museo-sans, "Area Variable", "Lato", sans-serif',
        fontWeight: 500,
        lineHeight: '24px',
        letterSpacing: '0.01em',
        margin: 0,
        textTransform: 'capitalize',
    },
    creditText: {
        fontFamily: 'museo-sans, "Area Variable", "Lato", sans-serif',
        fontWeight: 400,
        fontSize: '15px!important',
        // color is now dynamically set via inline style
        margin: 0,
    },
    creditHighlight: {
        fontFamily: 'museo-sans, "Area Variable", "Lato", sans-serif',
        fontWeight: 700,
        fontSize: '15px !important',
        textAlign: 'center',
        // color is now dynamically set via inline style
    },
    secondaryCreditText: {
        fontSize: '13px !important',
    },
});

/**
 * TalonOneBanner - Self-contained banner component that fetches and displays
 * TalonOne rewards data. Only renders when the user has available rewards.
 *
 * Uses Redux with Saga for rewards data fetching.
 * Uses Apollo/GraphQL for ContentStack styling content.
 * Automatically fetches rewards when user is authenticated & navigates to account page.
 */
const TalonOneBanner = ({
    classes,
    firstName,
}) => {
    const dispatch = useDispatch();

    // Redux selectors for rewards data
    const isLoading = useSelector(getTalonOneRewardsLoading);
    const isError = useSelector(getTalonOneRewardsError);
    const rewardsData = useSelector(getTalonOneRewardsData);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);

    // Sum currentBalance from all loyalty programs
    const programs = rewardsData?.loyalty?.programs || {};
    const rewardsBalance = Object.values(programs).reduce(
        (sum, program) => sum + (program?.ledger?.currentBalance || 0),
        0,
    );

    // Fetch Talon One banner content from ContentStack
    const TALON_ONE_BANNER_QUERY = findContent({
        brand: 'global',
        contentType: 'talon_one',
    });

    const { data: bannerContentData } = useQuery(TALON_ONE_BANNER_QUERY);

    // Fetch rewards data on every component mount when user is authenticated
    // This ensures fresh data on page refresh or component re-render
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchTalonOneRewards());
        }

        // Clear rewards data when component unmounts to ensure fresh fetch next time
        return () => {
            dispatch(clearTalonOneRewards());
        };
    }, [dispatch, isAuthenticated]);

    // Extract my_account content from GraphQL response
    const myAccountContent = bannerContentData?.findContent?.content?.entries?.[0]?.my_account;
    const myAccountStyling = bannerContentData?.findContent?.content?.entries?.[0]?.styling;

    // Extract styling values from ContentStack with fallbacks
    const backgroundColor = myAccountStyling?.background_color || '#EFF9F6';
    const creditColor = myAccountStyling?.credit_color || '#387154';
    const textColor = myAccountStyling?.text_color || '#000000';
    const iconUrl = myAccountContent?.icon?.url || null;
    const creditMessage = myAccountContent?.credit_message || 'You have a {{credit}} credit to use today!';
    const secondaryCreditMessage = myAccountContent?.secondary_credit_message || 'Redeem at checkout';

    // Don't render if no rewards or Contentstack query loading || error
    if (rewardsBalance <= 0 || isLoading || isError) {
        return null;
    }

    // Split credit message by placeholder
    // Handle all patterns: {credit}, ${credit}, {{credit}}, ${{credit}}
    const parts = creditMessage?.split(/\$?\{\{?credit\}\}?/) || '';
    const beforeCredit = parts[0] || '';
    const afterCredit = parts[1] || '';
    const creditAmount = `$${rewardsBalance} `;

    return (
        <div
            className={classes.accountBannerHeader}
            style={{ backgroundColor }}
            role="region"
            aria-label={`${firstName}'s account - You have ${creditAmount} waiting`}
        >
            <div className={classes.bannerTextContent}>
                <p className={classes.creditText} style={{ color: textColor }}>
                    {beforeCredit}
                    <span className={classes.creditHighlight} style={{ color: creditColor }}>
                        {creditAmount}
                    </span>
                    {afterCredit}
                </p>
                <p className={`${classes.creditText} ${classes.secondaryCreditText}`} style={{ color: textColor }}>
                    {secondaryCreditMessage}
                </p>
            </div>
            {iconUrl && (
                <div className={classes.bannerIcon}>
                    <img src={iconUrl} alt="Rewards icon" />
                </div>
            )}
        </div>
    );
};

TalonOneBanner.defaultProps = {
    firstName: '',
};

TalonOneBanner.propTypes = {
    classes: shape({}).isRequired,
    firstName: string,
};

export default withStyles(styles)(TalonOneBanner);
